import { Box, Stack, Text, styled } from '@ltvco/refresh-lib/theme';

import {
  CLASS_ACTION_REPORT_SECTION_IDS,
  META_BODY,
} from '../shared/constants';
import { DesktopReportSection } from '../shared/DesktopReportSection';

import type { DocketDetailKeyDetailsSection } from '../types';

const KEY_DETAIL_LABELS: Record<string, string> = {
  case_name: 'Case Name',
  court: 'Court',
  docket_number: 'Docket Number',
  filed: 'Filed',
  case_type: 'Type of Case',
  magistrate_judge: 'Magistrate Judge',
  assigned_judge: 'Assigned Judge',
  jurisdiction: 'Jurisdiction',
  jury_demand: 'Jury Demand',
};

const LEFT_COLUMN_KEYS: Array<keyof DocketDetailKeyDetailsSection> = [
  'case_name',
  'court',
  'docket_number',
  'filed',
];

const KeyDetailItem = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(0.5),
  paddingTop: theme.spacing(1.5),
  paddingBottom: theme.spacing(1.5),
  '&:first-of-type': {
    paddingTop: 0,
  },
}));

const KeyDetailLabelText = styled(Text)(() => ({
  fontWeight: 400,
  fontSize: 14,
  lineHeight: '16px',
  fontFamily: "'Nunito Sans', sans-serif",
  color: META_BODY,
}));

const KeyDetailValueText = styled(Text)(({ theme }) => ({
  fontSize: 16,
  fontFamily: "'Nunito Sans', sans-serif",
  lineHeight: '24px',
  color: theme.palette.text.primary,
}));

function judgeField(key_details: DocketDetailKeyDetailsSection): {
  label: string;
  value: string;
} | null {
  const magistrate = key_details.magistrate_judge?.trim();
  if (magistrate) {
    return {
      label: KEY_DETAIL_LABELS.magistrate_judge,
      value: magistrate,
    };
  }
  const assigned = key_details.assigned_judge?.trim();
  if (assigned) {
    return {
      label: KEY_DETAIL_LABELS.assigned_judge,
      value: assigned,
    };
  }
  return null;
}

function buildField(
  label: string,
  value?: string
): { label: string; value: string } | null {
  const trimmed = value?.trim();
  return trimmed ? { label, value: trimmed } : null;
}

function KeyDetailField({ label, value }: { label: string; value: string }) {
  return (
    <KeyDetailItem>
      <KeyDetailLabelText>{label}</KeyDetailLabelText>
      <KeyDetailValueText>{value}</KeyDetailValueText>
    </KeyDetailItem>
  );
}

export function KeyDetailsSection({
  key_details,
}: {
  key_details: DocketDetailKeyDetailsSection;
}) {
  const leftFields = LEFT_COLUMN_KEYS.filter((k) => key_details[k]?.trim()).map(
    (k) => ({
      label: KEY_DETAIL_LABELS[k],
      value: (key_details[k] as string).trim(),
    })
  );

  const rightFields: Array<{ label: string; value: string }> = [
    buildField(KEY_DETAIL_LABELS.case_type, key_details.case_type),
    judgeField(key_details),
    buildField(KEY_DETAIL_LABELS.jurisdiction, key_details.jurisdiction),
    buildField(KEY_DETAIL_LABELS.jury_demand, key_details.jury_demand),
  ].filter(Boolean) as Array<{ label: string; value: string }>;

  if (!leftFields.length && !rightFields.length) {
    return null;
  }

  const useTwoColumnLayout = leftFields.length > 0 && rightFields.length > 0;

  return (
    <DesktopReportSection
      title="Key Details"
      sectionId={CLASS_ACTION_REPORT_SECTION_IDS.key_details}
    >
      <Box
        sx={(theme) => ({
          display: 'grid',
          gridTemplateColumns: '1fr',
          columnGap: theme.spacing(3),
          width: '100%',
          pt: 0.5,
          ...(useTwoColumnLayout && {
            [theme.breakpoints.up('sm')]: {
              gridTemplateColumns: '1fr 1fr',
            },
          }),
        })}
      >
        {leftFields.length > 0 ? (
          <Stack sx={{ minWidth: 0 }}>
            {leftFields.map((f) => (
              <KeyDetailField key={f.label} label={f.label} value={f.value} />
            ))}
          </Stack>
        ) : null}
        {rightFields.length > 0 ? (
          <Stack sx={{ minWidth: 0 }}>
            {rightFields.map((f, i) => (
              <KeyDetailField
                key={`${f.label}-${i}`}
                label={f.label}
                value={f.value}
              />
            ))}
          </Stack>
        ) : null}
      </Box>
    </DesktopReportSection>
  );
}
